/**
 * Travel information constants.
 * Extracted from WanderAppComplete.jsx for shared use by web and mobile.
 */

import { COUNTRY_TO_CURRENCY as _SOURCE_COUNTRY_TO_CURRENCY } from './itineraryConstants.js';

// ============================================================
// Country to currency mapping (Title-Case keys → ISO code string)
// ============================================================
// PR #291 — single-source-of-truth: this map is derived from
// `itineraryConstants.js`'s COUNTRY_TO_CURRENCY (which is the canonical
// source — lowercase keys + full objects with code/symbol/approxUsdRate).
// Adding a new country requires editing ONE file (`itineraryConstants.js`)
// not three. Resolves the recurring "Bhutan renders as USD on web but
// correctly on backend tip-gen" class of drift bug — UAT May 2026.
//
// Special-cased Title-Case mapping for keys that don't follow the
// "first-letter capitalized" rule (UAE, UK, USA — acronyms).
const TITLE_CASE_OVERRIDES = {
  'usa': 'USA',
  'uk': 'UK',
  'uae': 'UAE',
};
function titleCaseCountry(lowerKey) {
  if (TITLE_CASE_OVERRIDES[lowerKey]) return TITLE_CASE_OVERRIDES[lowerKey];
  return lowerKey.split(' ').map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
}

export const COUNTRY_TO_CURRENCY = Object.freeze(
  Object.fromEntries(
    Object.entries(_SOURCE_COUNTRY_TO_CURRENCY).map(([country, info]) => [
      titleCaseCountry(country),
      info.code,
    ])
  )
);

// Schengen area countries (no border checks between them)
export const SCHENGEN_COUNTRIES = [
  'Austria', 'Belgium', 'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France', 'Germany',
  'Greece', 'Hungary', 'Iceland', 'Italy', 'Latvia', 'Liechtenstein', 'Lithuania', 'Luxembourg',
  'Malta', 'Netherlands', 'Norway', 'Poland', 'Portugal', 'Slovakia', 'Slovenia', 'Spain', 'Sweden', 'Switzerland'
];

// Visa requirements from US passport (simplified)
export const VISA_REQUIREMENTS_US = {
  // Visa-free or visa on arrival (90 days or less)
  visaFree: [
    'Argentina', 'Chile', 'Peru', 'Colombia', 'Brazil', 'Mexico', 'Canada',
    'France', 'Italy', 'Spain', 'Germany', 'UK', 'Netherlands', 'Portugal', 'Greece', 'Switzerland',
    'Austria', 'Belgium', 'Czech Republic', 'Denmark', 'Finland', 'Hungary', 'Iceland', 'Ireland',
    'Norway', 'Poland', 'Sweden',
    'Japan', 'South Korea', 'Taiwan', 'Singapore', 'Hong Kong', 'Malaysia', 'Thailand',
    'UAE', 'Morocco', 'South Africa', 'Australia', 'New Zealand'
  ],
  // E-Visa required (apply online before travel)
  eVisa: ['India', 'Vietnam', 'Cambodia', 'Turkey', 'Egypt', 'Kenya', 'Sri Lanka', 'Myanmar'],
  // Visa required (must apply at embassy)
  visaRequired: ['China', 'Russia', 'Saudi Arabia', 'Iran', 'North Korea', 'Cuba']
};

/**
 * Look up currency code for a country name.
 * @param {string} country - Country name (e.g., "France", "Japan")
 * @returns {string|null} ISO 4217 currency code or null
 */
export function getCurrencyForCountry(country) {
  if (!country) return null;
  // Try exact match first (keys are Title Case)
  const direct = COUNTRY_TO_CURRENCY[country];
  if (direct) return direct;
  // Try case-insensitive match
  const lower = country.toLowerCase().trim();
  for (const [key, value] of Object.entries(COUNTRY_TO_CURRENCY)) {
    if (key.toLowerCase() === lower) return value;
  }
  return null;
}

// Border crossing info for common routes
export const BORDER_CROSSING_NOTES = {
  'Schengen': 'No passport control between Schengen countries. Keep passport handy for ID checks.',
  'UK-Schengen': 'Passport control required. UK is not in Schengen - expect immigration checks.',
  'US-Canada': 'Valid passport required. NEXUS card speeds up crossing.',
  'US-Mexico': 'Valid passport required. Consider Global Entry for faster return.',
};
